import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { uploadPhoto } from "@/lib/uploadPhoto";
import { PasteableInput } from "@/components/PasteableInput";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash, Tag, HardDrives, ImageSquare, ArrowsClockwise, Cloud, CheckCircle, Warning, CreditCard, Copy, Sparkle } from "@phosphor-icons/react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useAuth, hasRole } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Settings() {
  const { user } = useAuth();
  const isSuper = hasRole(user, "super_admin");

  const [rate, setRate] = useState("");
  const [categories, setCategories] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [counts, setCounts] = useState({});
  const [toDelete, setToDelete] = useState(null);

  const loadAll = useCallback(async () => {
    if (isSuper) {
      const s = await api.get("/settings");
      setRate(s.data.exchange_rate);
    }
    const c = await api.get("/categories");
    setCategories(c.data);
    try {
      const p = await api.get("/products");
      const map = {};
      p.data.forEach((prod) => {
        const k = prod.category || "Sin categoría";
        map[k] = (map[k] || 0) + 1;
      });
      setCounts(map);
    } catch (err) { console.warn("Settings stats load failed:", err); }
  }, [isSuper]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const saveRate = async (e) => {
    e.preventDefault();
    try {
      await api.put("/settings", { exchange_rate: parseFloat(rate) });
      toast.success("Tipo de cambio actualizado");
    } catch { toast.error("Error"); }
  };

  const addCat = async (e) => {
    e.preventDefault();
    const name = newCat.trim();
    if (!name) return;
    try {
      await api.post("/categories", { name });
      toast.success(`Categoría "${name}" creada`);
      setNewCat("");
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error al crear"); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/categories/${encodeURIComponent(toDelete)}`);
      toast.success(`Categoría "${toDelete}" eliminada`);
      loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al eliminar");
    } finally {
      setToDelete(null);
    }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6 max-w-4xl">
      <header>
        <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1">CONFIGURACIÓN</div>
        <h1 className="font-heading text-4xl font-extrabold tracking-tight">Ajustes</h1>
      </header>

      {isSuper && (
        <section className="border border-zinc-200 p-6 bg-white">
          <h3 className="font-heading text-lg font-semibold mb-1">Tipo de cambio</h3>
          <p className="text-xs text-zinc-500 mb-4">Se usa para calcular valor total del inventario en ambas monedas.</p>
          <form onSubmit={saveRate} className="flex gap-3 items-end">
            <div className="flex-1 max-w-xs">
              <Label className="text-xs uppercase tracking-wider">1 USD equivale a (MXN)</Label>
              <Input type="number" step="0.01" value={rate} onChange={(e)=>setRate(e.target.value)} required data-testid="settings-rate-input" className="mt-1 numeric" />
            </div>
            <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="settings-save-rate">Guardar</Button>
          </form>
        </section>
      )}

      {isSuper && (
        <section className="border border-zinc-200 p-6 bg-white">
          <h3 className="font-heading text-lg font-semibold mb-1">Datos de la empresa</h3>
          <p className="text-xs text-zinc-500 mb-4">Aparecerán en el encabezado del PDF de las notas/facturas.</p>
          <CompanyInfoEditor />
        </section>
      )}

      {isSuper && (
        <section className="border border-zinc-200 p-6 bg-white">
          <h3 className="font-heading text-lg font-semibold mb-1">Plantilla de Nota de Venta (PDF)</h3>
          <p className="text-xs text-zinc-500 mb-4">Configura el texto de pie de página por defecto que aparecerá en todas las notas de venta (garantías, agradecimientos, datos de contacto, etc.). El vendedor puede sobreescribirlo por nota individual.</p>
          <PdfFooterEditor />
        </section>
      )}

      <section className="border border-zinc-200 bg-white">
        <div className="px-6 pt-6 pb-4">
          <h3 className="font-heading text-lg font-semibold mb-1">Categorías de inventario</h3>
          <p className="text-xs text-zinc-500 mb-4">Administra las etiquetas que se asignan a tus productos.</p>
          <form onSubmit={addCat} className="flex gap-2 max-w-xl">
            <Input
              value={newCat}
              onChange={(e)=>setNewCat(e.target.value)}
              placeholder="Ej. Cables HDMI, Discos SSD, Auriculares…"
              data-testid="new-cat-input"
            />
            <Button type="submit" className="bg-[#002FA7] hover:bg-[#002277] text-white gap-1 shrink-0" data-testid="add-cat-btn">
              <Plus size={14} weight="bold" /> Agregar
            </Button>
          </form>
        </div>
        <div className="border-t border-zinc-200">
          <div className="px-6 py-3 bg-zinc-50 flex items-center justify-between">
            <span className="text-xs uppercase font-mono-data text-zinc-500">Categorías existentes</span>
            <span className="text-xs text-zinc-500 font-mono-data">{categories.length} total</span>
          </div>
          {categories.length === 0 ? (
            <div className="text-center py-12 text-zinc-400 text-sm dotted-bg m-4">No hay categorías. Crea la primera arriba.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-white text-[10px] uppercase tracking-wider text-zinc-500 border-b border-zinc-100">
                <tr>
                  <th className="text-left px-6 py-2.5">Categoría</th>
                  <th className="text-right px-6 py-2.5">Productos asociados</th>
                  <th className="text-right px-6 py-2.5 w-24"></th>
                </tr>
              </thead>
              <tbody>
                {categories.map((c) => (
                  <tr key={c} className="border-t border-zinc-100 hover:bg-zinc-50" data-testid={`cat-row-${c}`}>
                    <td className="px-6 py-3 font-medium inline-flex items-center gap-2">
                      <Tag size={14} className="text-zinc-400" /> {c}
                    </td>
                    <td className="px-6 py-3 text-right numeric text-zinc-600">{counts[c] || 0}</td>
                    <td className="px-6 py-3 text-right">
                      <button
                        onClick={()=>setToDelete(c)}
                        className="text-zinc-400 hover:text-[#E0000F] p-1.5 hover:bg-red-50 rounded"
                        title="Eliminar categoría"
                        data-testid={`del-cat-${c}`}
                      >
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </section>

      {isSuper && <StorageUsage />}

      {isSuper && <CloudinaryConfig />}

      {isSuper && <MercadoPagoConfig />}

      <AlertDialog open={!!toDelete} onOpenChange={(o)=>{ if (!o) setToDelete(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar categoría?</AlertDialogTitle>
            <AlertDialogDescription>
              Se eliminará la categoría <b>"{toDelete}"</b>. Los productos que la tengan asignada conservarán el nombre como texto, pero ya no aparecerá en el dropdown del formulario.
              {counts[toDelete] > 0 && (
                <span className="block mt-2 text-[#E0000F] font-semibold">
                  ⚠ {counts[toDelete]} producto(s) están usando esta categoría.
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-[#E0000F] hover:bg-[#b8000c] text-white">
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {isSuper && <AlendraAISettings />}
    </div>
  );
}

function PdfFooterEditor() {
  const [footer, setFooter] = useState("");
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      setFooter(r.data?.pdf_default_footer || "");
      setLoaded(true);
    });
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", { pdf_default_footer: footer });
      toast.success("Pie de página actualizado");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  if (!loaded) return <div className="text-xs text-zinc-400">Cargando…</div>;

  return (
    <form onSubmit={save} className="space-y-3">
      <textarea
        value={footer}
        onChange={(e)=>setFooter(e.target.value)}
        rows={5}
        className="w-full border border-zinc-300 rounded px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-[#002FA7] focus:border-[#002FA7]"
        placeholder="Ej.&#10;3 meses de garantía contra defectos de fábrica.&#10;Gracias por confiar en Alendra PC.&#10;Soporte: contacto@alendrapc.com · WhatsApp 55-1234-5678"
        data-testid="settings-pdf-footer"
      />
      <div className="flex justify-end">
        <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="settings-save-footer">
          {saving ? "Guardando…" : "Guardar pie de página"}
        </Button>
      </div>
    </form>
  );
}

function CompanyInfoEditor() {
  const [form, setForm] = useState({
    company_name: "", company_phone: "", company_email: "",
    company_address: "", company_city: "", company_country: "", company_rfc: "",
    social_instagram: "", social_facebook: "", social_tiktok: "",
    company_logo: "",
    apc_metrics: {},
  });
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => {
      const s = r.data || {};
      setForm({
        company_name: s.company_name || "",
        company_phone: s.company_phone || "",
        company_email: s.company_email || "",
        company_address: s.company_address || "",
        company_city: s.company_city || "",
        company_country: s.company_country || "",
        company_rfc: s.company_rfc || "",
        company_logo: s.company_logo || "",
        social_instagram: s.social_instagram || "",
        social_facebook: s.social_facebook || "",
        social_tiktok: s.social_tiktok || "",
        apc_metrics: s.apc_metrics || {},
      });
      setLoaded(true);
    });
  }, []);

  const onLogoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 600 * 1024) {
      toast.error("La imagen es muy grande (máx 600 KB)");
      return;
    }
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      toast.error("Formato no soportado (usa PNG, JPG o WEBP)");
      return;
    }
    try {
      const result = await uploadPhoto(file, { folder: "alendrapc/logo", maxSide: 600, quality: 0.9 });
      setForm((f) => ({ ...f, company_logo: result }));
    } catch (err) {
      toast.error(err?.message || "No se pudo subir el logo");
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings", form);
      toast.success("Datos de empresa actualizados");
    } catch { toast.error("Error al guardar"); }
    finally { setSaving(false); }
  };

  if (!loaded) return <div className="text-xs text-zinc-400">Cargando…</div>;

  const field = (k, label, ph = "", helper = "") => (
    <div>
      <Label className="text-xs uppercase tracking-wider">{label}</Label>
      <PasteableInput
        value={form[k]}
        onChange={(e) => setForm({ ...form, [k]: e.target.value })}
        placeholder={ph}
        className="mt-1"
        data-testid={`settings-${k.replaceAll("_", "-")}`}
      />
      {helper && <p className="text-[10px] text-zinc-400 mt-1">{helper}</p>}
    </div>
  );

  return (
    <form onSubmit={save} className="space-y-4">
      <div>
        <Label className="text-xs uppercase tracking-wider">Logo de la empresa (PNG/JPG · máx 600KB)</Label>
        <div className="mt-2 flex items-start gap-4">
          <div className="w-32 h-20 border border-zinc-200 rounded flex items-center justify-center bg-zinc-50 overflow-hidden">
            {form.company_logo ? (
              <img src={form.company_logo} alt="Logo" className="max-w-full max-h-full object-contain" />
            ) : (
              <span className="text-[10px] text-zinc-400 text-center px-2">sin logo</span>
            )}
          </div>
          <div className="flex flex-col gap-2 flex-1">
            <input
              type="file"
              accept="image/png,image/jpeg,image/webp"
              onChange={onLogoChange}
              className="text-xs file:mr-3 file:px-3 file:py-1.5 file:border-0 file:bg-[#002FA7] file:text-white file:rounded file:cursor-pointer file:hover:bg-[#002277]"
              data-testid="settings-logo-upload"
            />
            {form.company_logo && (
              <button type="button" onClick={() => setForm({ ...form, company_logo: "" })} className="text-[11px] text-[#E0000F] hover:underline self-start">
                Quitar logo
              </button>
            )}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {field("company_name", "Nombre / Encabezado en PDFs", "Alendra PC", "Aparece como título en el encabezado de las notas de venta (donde hoy se ve 'Inventario Gadgets & Cómputo').")}
        {field("company_rfc", "RFC", "")}
        {field("company_phone", "Teléfono", "+52 ...")}
        {field("company_email", "Email", "contacto@empresa.com")}
        {field("company_address", "Dirección", "Calle, número, colonia")}
        {field("company_city", "Ciudad / Estado / CP", "Tijuana, BC 22223")}
        {field("company_country", "País", "México")}
        <div className="sm:col-span-2 pt-2 mt-2 border-t border-zinc-200">
          <p className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500 mb-3">Redes sociales (aparecen en la landing pública)</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {field("social_instagram", "Instagram", "https://instagram.com/alendrapcmx")}
            {field("social_facebook", "Facebook", "https://facebook.com/alendrapcmx")}
            {field("social_tiktok", "TikTok", "https://tiktok.com/@alendrapc")}
          </div>
        </div>
        <div className="sm:col-span-2 pt-2 mt-2 border-t border-zinc-200">
          <p className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500 mb-1">Métricas del esquema de inversión</p>
          <p className="text-[10px] text-zinc-400 mb-3">Aparecen en <code className="bg-zinc-100 px-1 rounded">/esquemadeinversion</code> con animaciones de conteo.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              ["clients",        "Clientes atendidos",  "1500"],
              ["equipment_sold", "Equipos vendidos",    "3000"],
              ["states",         "Estados atendidos",   "8"],
              ["years",          "Años de experiencia", "5"],
            ].map(([key, lbl, ph]) => (
              <div key={key}>
                <Label className="text-xs uppercase tracking-wider">{lbl}</Label>
                <PasteableInput
                  type="number"
                  value={(form.apc_metrics && form.apc_metrics[key]) ?? ""}
                  onChange={(e) => setForm({ ...form, apc_metrics: { ...(form.apc_metrics || {}), [key]: Number(e.target.value) || 0 } })}
                  placeholder={ph}
                  className="mt-1"
                  data-testid={`apc-metric-${key}`}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="flex justify-end">
        <Button type="submit" disabled={saving} className="bg-[#002FA7] hover:bg-[#002277] text-white" data-testid="settings-save-company">
          {saving ? "Guardando…" : "Guardar datos de empresa"}
        </Button>
      </div>
    </form>
  );
}

// === Storage usage report ===
const fmtBytes = (n) => {
  const b = Number(n) || 0;
  if (b < 1024) return `${b} B`;
  const kb = b / 1024;
  if (kb < 1024) return `${kb.toFixed(1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(2)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
};

const STORAGE_COLORS = ["#002FA7", "#00873D", "#E0000F", "#FFC000", "#7E57C2", "#1E88E5", "#FF7043", "#26A69A", "#DB2777", "#52525B"];

function StorageUsage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/system/storage");
      setData(r.data);
    } catch (e) {
      toast.error("No se pudo cargar el uso de espacio");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = data?.total_data_bytes || 0;
  const photosBytes = data?.photos?.bytes || 0;
  const photoCount = data?.photos?.count || 0;
  const photoPct = total > 0 ? (photosBytes / total) * 100 : 0;
  const cols = (data?.collections || []).filter((c) => c.data_bytes > 0);

  return (
    <section className="border border-zinc-200 bg-white" data-testid="storage-usage">
      <header className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2">
            <HardDrives size={18} weight="duotone" className="text-[#002FA7]" />
            Uso de espacio
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">Cuánto pesa el sistema y dónde está concentrado.</p>
        </div>
        <Button variant="outline" size="sm" onClick={load} className="gap-1.5" disabled={loading} data-testid="refresh-storage">
          <ArrowsClockwise size={14} /> {loading ? "Cargando…" : "Actualizar"}
        </Button>
      </header>

      <div className="p-6 space-y-6">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="border border-zinc-200 p-4">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Total usado</div>
            <div className="font-heading text-3xl font-extrabold tracking-tight numeric mt-1" data-testid="total-used">{fmtBytes(total)}</div>
            <div className="text-[10px] text-zinc-500 mt-1">Tamaño real de los datos (sin índices).</div>
          </div>
          <div className="border border-zinc-200 p-4">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500">Reservado en disco</div>
            <div className="font-heading text-3xl font-extrabold tracking-tight numeric mt-1">{fmtBytes(data?.total_storage_bytes || 0)}</div>
            <div className="text-[10px] text-zinc-500 mt-1">Espacio asignado por MongoDB (incluye compresión / overhead).</div>
          </div>
          <div className="border border-zinc-200 p-4">
            <div className="text-[10px] font-mono-data uppercase tracking-wider text-zinc-500 inline-flex items-center gap-1">
              <ImageSquare size={11} /> Fotos de productos
            </div>
            <div className="font-heading text-3xl font-extrabold tracking-tight numeric mt-1">{fmtBytes(photosBytes)}</div>
            <div className="text-[10px] text-zinc-500 mt-1">
              {photoCount} imágenes · <b>{photoPct.toFixed(1)}%</b> del total
            </div>
          </div>
        </div>

        <div>
          <div className="text-xs font-mono-data uppercase tracking-wider text-zinc-500 mb-2">Distribución por sección</div>
          <div className="flex h-3 w-full rounded-sm overflow-hidden border border-zinc-200">
            {cols.map((c, idx) => {
              const pct = total > 0 ? (c.data_bytes / total) * 100 : 0;
              if (pct < 0.2) return null;
              return (
                <div
                  key={c.name}
                  style={{ width: `${pct}%`, background: STORAGE_COLORS[idx % STORAGE_COLORS.length] }}
                  title={`${c.label}: ${fmtBytes(c.data_bytes)} (${pct.toFixed(1)}%)`}
                />
              );
            })}
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="storage-table">
            <thead className="bg-zinc-50 text-xs uppercase text-zinc-500">
              <tr>
                <th className="text-left px-4 py-2.5">Sección</th>
                <th className="text-right px-4 py-2.5">Registros</th>
                <th className="text-right px-4 py-2.5">Datos</th>
                <th className="text-right px-4 py-2.5">% del total</th>
                <th className="text-right px-4 py-2.5">Reservado</th>
              </tr>
            </thead>
            <tbody>
              {cols.map((c, idx) => {
                const pct = total > 0 ? (c.data_bytes / total) * 100 : 0;
                return (
                  <tr key={c.name} className="border-t border-zinc-100">
                    <td className="px-4 py-2.5">
                      <span className="inline-flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: STORAGE_COLORS[idx % STORAGE_COLORS.length] }} />
                        <span className="font-medium">{c.label}</span>
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right numeric text-zinc-600">{c.count.toLocaleString("es-MX")}</td>
                    <td className="px-4 py-2.5 text-right numeric font-semibold">{fmtBytes(c.data_bytes)}</td>
                    <td className="px-4 py-2.5 text-right numeric text-zinc-600">{pct.toFixed(1)}%</td>
                    <td className="px-4 py-2.5 text-right numeric text-zinc-500 text-xs">{fmtBytes(c.storage_bytes)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}




// === Cloudinary configuration ===
function CloudinaryConfig() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ cloud_name: "", api_key: "", api_secret: "" });
  const [saving, setSaving] = useState(false);
  const [migrating, setMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/cloudinary/status");
      setStatus(data);
      if (data?.cloud_name) {
        setForm((f) => ({ ...f, cloud_name: data.cloud_name }));
      }
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const save = async (e) => {
    e.preventDefault();
    if (!form.cloud_name || !form.api_key || !form.api_secret) {
      toast.error("Llena los 3 campos");
      return;
    }
    setSaving(true);
    try {
      await api.put("/cloudinary/config", form);
      toast.success("Conectado con Cloudinary");
      setForm((f) => ({ ...f, api_secret: "" }));
      // Invalidate the cached status so the next upload uses Cloudinary
      const { resetCloudinaryStatus } = await import("@/lib/uploadPhoto");
      resetCloudinaryStatus();
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando credenciales");
    } finally {
      setSaving(false);
    }
  };

  const migrate = async () => {
    if (!window.confirm("¿Migrar todas las fotos actuales a Cloudinary? Este proceso puede tardar 1–2 minutos. Las fotos seguirán funcionando igual después.")) return;
    setMigrating(true);
    setMigrationResult(null);
    try {
      const { data } = await api.post("/cloudinary/migrate", null, { timeout: 300000 });
      setMigrationResult(data);
      toast.success(`Migración lista: ${data.migrated} fotos subidas`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error en la migración");
    } finally {
      setMigrating(false);
    }
  };

  const isConfigured = status?.configured;

  return (
    <section className="border border-zinc-200 bg-white" data-testid="cloudinary-config">
      <header className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2">
            <Cloud size={18} weight="duotone" className="text-[#002FA7]" />
            Almacenamiento de fotos · Cloudinary
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Sube las fotos a un CDN externo para no inflar la base de datos. Plan free: 25 GB de almacenamiento y 25 GB de ancho de banda al mes.
          </p>
        </div>
        {isConfigured ? (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00873D] bg-[#00873D]/10 px-3 py-1.5 rounded-sm">
            <CheckCircle size={14} weight="fill" /> Conectado · {status?.cloud_name}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FFC000] bg-[#FFC000]/10 px-3 py-1.5 rounded-sm">
            <Warning size={14} weight="fill" /> Sin configurar
          </span>
        )}
      </header>

      <div className="p-6 space-y-4">
        {!isConfigured && (
          <div className="border border-blue-200 bg-blue-50 rounded p-4 text-sm">
            <p className="font-semibold text-blue-900 mb-2">Cómo obtener los datos:</p>
            <ol className="list-decimal ml-5 text-blue-900 text-xs space-y-1">
              <li>Entra a <a href="https://cloudinary.com/users/register/free" target="_blank" rel="noreferrer" className="underline font-semibold">cloudinary.com/users/register/free</a> y crea una cuenta (sin tarjeta).</li>
              <li>En el Dashboard, busca <b>"Account Details"</b> o <b>"API Environment variable"</b>.</li>
              <li>Copia: <code>Cloud Name</code>, <code>API Key</code> y <code>API Secret</code> (haz click en el ojito 👁 para revelar el secret).</li>
              <li>Pega los 3 abajo y guarda. Verificaremos las credenciales con una llamada de prueba.</li>
            </ol>
          </div>
        )}

        <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <Label className="text-xs uppercase tracking-wider">Cloud name *</Label>
            <Input
              value={form.cloud_name}
              onChange={(e) => setForm({ ...form, cloud_name: e.target.value.trim() })}
              placeholder="ej. alendrapc"
              className="mt-1 font-mono-data text-sm"
              data-testid="cloudinary-cloud-name"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">API Key *</Label>
            <Input
              value={form.api_key}
              onChange={(e) => setForm({ ...form, api_key: e.target.value.trim() })}
              placeholder="123456789012345"
              className="mt-1 font-mono-data text-sm"
              data-testid="cloudinary-api-key"
            />
          </div>
          <div>
            <Label className="text-xs uppercase tracking-wider">API Secret *</Label>
            <Input
              type="password"
              value={form.api_secret}
              onChange={(e) => setForm({ ...form, api_secret: e.target.value.trim() })}
              placeholder={isConfigured ? "•••••• (oculto)" : "Pega tu API secret"}
              className="mt-1 font-mono-data text-sm"
              data-testid="cloudinary-api-secret"
              autoComplete="off"
            />
          </div>
          <div className="sm:col-span-3 flex justify-end gap-2">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#002FA7] hover:bg-[#002277] text-white"
              data-testid="cloudinary-save"
            >
              {saving ? "Probando conexión…" : isConfigured ? "Actualizar credenciales" : "Conectar y probar"}
            </Button>
          </div>
        </form>

        {isConfigured && (
          <div className="border-t border-zinc-200 pt-4 mt-2">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-semibold text-zinc-800">Migrar fotos existentes</h3>
                <p className="text-xs text-zinc-500 mt-0.5">
                  Toma las fotos guardadas como base64 dentro de MongoDB y las sube a tu cuenta de Cloudinary. Se conservan las URLs ya migradas. Las fotos nuevas siempre irán directo a Cloudinary.
                </p>
              </div>
              <Button
                onClick={migrate}
                disabled={migrating}
                variant="outline"
                className="gap-2"
                data-testid="cloudinary-migrate"
              >
                <ArrowsClockwise size={14} /> {migrating ? "Migrando…" : "Migrar fotos ahora"}
              </Button>
            </div>
            {migrationResult && (
              <div className="mt-3 text-sm bg-zinc-50 border border-zinc-200 rounded p-3">
                <div className="text-[#00873D] font-semibold">✓ Migración terminada</div>
                <ul className="text-xs text-zinc-700 mt-1 space-y-0.5">
                  <li>Fotos migradas: <b>{migrationResult.migrated}</b></li>
                  <li>Ya estaban en Cloudinary: <b>{migrationResult.skipped_already_uploaded}</b></li>
                  {migrationResult.errors?.length > 0 && (
                    <li className="text-[#E0000F]">Errores: {migrationResult.errors.length}</li>
                  )}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
}



// === Mercado Pago configuration ===
function MercadoPagoConfig() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ access_token: "", public_key: "", shipping_cost_mxn: "", shipping_bc_cost_mxn: "", shipping_local: "", pickup_address: "" });
  const [saving, setSaving] = useState(false);
  const [savingDelivery, setSavingDelivery] = useState(false);
  const [copied, setCopied] = useState(false);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await api.get("/mercadopago/status");
      setStatus(data);
      setForm((f) => ({
        ...f,
        public_key: data?.public_key || "",
        shipping_cost_mxn: String(data?.shipping_cost_mxn ?? "150"),
        shipping_bc_cost_mxn: String(data?.shipping?.bc_cost_mxn ?? "250"),
        shipping_local: (data?.shipping?.local_municipalities || []).join(", "),
        pickup_address: data?.pickup_address || "",
      }));
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const saveCredentials = async (e) => {
    e.preventDefault();
    if (!form.access_token.trim() && !form.public_key.trim()) {
      toast.error("Pega al menos uno de los dos campos");
      return;
    }
    setSaving(true);
    try {
      const payload = {};
      if (form.access_token.trim()) payload.access_token = form.access_token.trim();
      if (form.public_key.trim()) payload.public_key = form.public_key.trim();
      await api.put("/mercadopago/config", payload);
      toast.success("Credenciales de Mercado Pago actualizadas");
      setForm((f) => ({ ...f, access_token: "" })); // never keep the secret in the form
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando credenciales");
    } finally {
      setSaving(false);
    }
  };

  const saveDelivery = async (e) => {
    e.preventDefault();
    setSavingDelivery(true);
    try {
      await api.put("/mercadopago/config", {
        shipping_cost_mxn: parseFloat(form.shipping_cost_mxn) || 0,
        shipping_bc_cost_mxn: parseFloat(form.shipping_bc_cost_mxn) || 0,
        shipping_local_municipalities: form.shipping_local.split(",").map((m) => m.trim()).filter(Boolean),
        pickup_address: form.pickup_address.trim(),
      });
      toast.success("Configuración de entrega actualizada");
      await loadStatus();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando entrega");
    } finally {
      setSavingDelivery(false);
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(status?.webhook_url || "");
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch { /* ignore */ }
  };

  const isConfigured = status?.configured;
  const env = status?.environment;

  return (
    <section className="border border-zinc-200 bg-white" data-testid="mercadopago-config">
      <header className="px-6 py-4 border-b border-zinc-200 flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-heading text-xl font-semibold inline-flex items-center gap-2">
            <CreditCard size={18} weight="duotone" className="text-[#009EE3]" />
            Pagos online · Mercado Pago
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Procesa pagos con tarjeta, OXXO, SPEI y Mercado Crédito en la tienda pública. Las credenciales se cifran en la base de datos.
          </p>
        </div>
        {isConfigured ? (
          env === "production" ? (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#00873D] bg-[#00873D]/10 px-3 py-1.5 rounded-sm" data-testid="mp-status-pill">
              <CheckCircle size={14} weight="fill" /> Producción · Pagos reales
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FFC000] bg-[#FFC000]/10 px-3 py-1.5 rounded-sm" data-testid="mp-status-pill">
              <Warning size={14} weight="fill" /> Modo prueba (TEST)
            </span>
          )
        ) : (
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#FFC000] bg-[#FFC000]/10 px-3 py-1.5 rounded-sm" data-testid="mp-status-pill">
            <Warning size={14} weight="fill" /> Sin configurar
          </span>
        )}
      </header>

      <div className="p-6 space-y-6">
        {/* How-to */}
        <div className="border border-blue-200 bg-blue-50 rounded p-4 text-sm">
          <p className="font-semibold text-blue-900 mb-2">Cómo obtener tus credenciales de producción:</p>
          <ol className="list-decimal ml-5 text-blue-900 text-xs space-y-1">
            <li>Entra a <a href="https://www.mercadopago.com.mx/developers/panel/app" target="_blank" rel="noreferrer" className="underline font-semibold">mercadopago.com.mx/developers/panel/app</a> y abre tu aplicación.</li>
            <li>En el menú lateral, ve a <b>{`"Credenciales de producción"`}</b> (NO uses las de prueba si quieres cobrar dinero real).</li>
            <li>Activa producción si te lo pide (puede pedir RFC + CURP + datos del negocio).</li>
            <li>Copia <b>Access Token</b> (empieza con <code>APP_USR-</code>) y <b>Public Key</b> (también <code>APP_USR-</code>).</li>
            <li>Pégalas abajo. El access token se guarda enmascarado: una vez guardado nunca lo verás completo otra vez.</li>
          </ol>
          <p className="mt-2 text-[11px] text-blue-800 italic">⚠ Para probar primero sin cobrar dinero real, en la misma pantalla puedes generar credenciales <b>TEST</b> (empiezan con <code>TEST-</code>) y pegarlas aquí. El sistema detecta automáticamente el modo.</p>
        </div>

        {/* Credentials form */}
        <form onSubmit={saveCredentials} className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Access Token *</Label>
            <Input
              type="password"
              value={form.access_token}
              onChange={(e) => setForm({ ...form, access_token: e.target.value })}
              placeholder={isConfigured ? `Actual: ${status?.access_token_preview || "•••"} — pega uno nuevo para cambiarlo` : "APP_USR-XXXXXXXXXXXXXXXX-…-XXXXXXX"}
              className="mt-1 font-mono-data text-sm"
              data-testid="mp-access-token"
              autoComplete="off"
            />
            <p className="text-[10px] text-zinc-500 mt-1">Secreto. Se guarda enmascarado. Nunca lo expongas en el navegador.</p>
          </div>
          <div className="sm:col-span-2">
            <Label className="text-xs uppercase tracking-wider">Public Key *</Label>
            <Input
              value={form.public_key}
              onChange={(e) => setForm({ ...form, public_key: e.target.value })}
              placeholder="APP_USR-xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
              className="mt-1 font-mono-data text-sm"
              data-testid="mp-public-key"
            />
          </div>
          <div className="sm:col-span-2 flex justify-end">
            <Button
              type="submit"
              disabled={saving}
              className="bg-[#009EE3] hover:bg-[#0084c2] text-white"
              data-testid="mp-save-credentials"
            >
              {saving ? "Guardando…" : isConfigured ? "Actualizar credenciales" : "Guardar credenciales"}
            </Button>
          </div>
        </form>

        {/* Webhook URL */}
        {isConfigured && (
          <div className="border-t border-zinc-200 pt-5">
            <h3 className="font-semibold text-zinc-800 text-sm mb-1">3. Configura el webhook en Mercado Pago</h3>
            <p className="text-xs text-zinc-500 mb-3">
              En tu aplicación de Mercado Pago, ve a <b>{`"Notificaciones (Webhooks) → Configurar URLs"`}</b>, pega la URL de abajo y selecciona el evento <code>Pagos</code>.
            </p>
            <div className="flex gap-2">
              <Input
                readOnly
                value={status?.webhook_url || ""}
                className="font-mono-data text-xs bg-zinc-50"
                data-testid="mp-webhook-url"
              />
              <Button
                type="button"
                variant="outline"
                onClick={copyWebhook}
                className="gap-1.5 shrink-0"
                data-testid="mp-webhook-copy"
              >
                <Copy size={14} weight="bold" />
                {copied ? "¡Copiada!" : "Copiar"}
              </Button>
            </div>
          </div>
        )}

        {/* Delivery config */}
        <form onSubmit={saveDelivery} className="border-t border-zinc-200 pt-5 space-y-4">
          <div>
            <h3 className="font-semibold text-zinc-800 text-sm">4. Configuración de entrega</h3>
            <p className="text-xs text-zinc-500">Estos valores se muestran al cliente en el checkout.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs uppercase tracking-wider">Costo de envío nacional (MXN)</Label>
              <Input
                type="number"
                min="0"
                step="1"
                value={form.shipping_cost_mxn}
                onChange={(e) => setForm({ ...form, shipping_cost_mxn: e.target.value })}
                className="mt-1 numeric"
                data-testid="mp-shipping-cost"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Resto de la República (Sonora en adelante) cuando el producto no tiene su propio costo de envío.</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Costo de envío Baja California (MXN)</Label>
              <Input type="number" min="0" step="1" value={form.shipping_bc_cost_mxn} onChange={(e) => setForm({ ...form, shipping_bc_cost_mxn: e.target.value })} className="mt-1 numeric" data-testid="mp-shipping-bc" />
              <p className="text-[10px] text-zinc-500 mt-1">Ensenada, Mexicali, San Quintín… cuando el producto no define el suyo.</p>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-wider">Municipios con envío gratis (local)</Label>
              <Input value={form.shipping_local} onChange={(e) => setForm({ ...form, shipping_local: e.target.value })} placeholder="Tijuana, Playas de Rosarito, Rosarito, Tecate" className="mt-1" data-testid="mp-shipping-local" />
              <p className="text-[10px] text-zinc-500 mt-1">Separados por coma. El carrito cobra el envío más alto entre sus productos (un solo paquete).</p>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Dirección de recolección</Label>
              <Input
                value={form.pickup_address}
                onChange={(e) => setForm({ ...form, pickup_address: e.target.value })}
                placeholder="Av. Tecnología 123, Col. Centro, Querétaro"
                className="mt-1"
                data-testid="mp-pickup-address"
              />
              <p className="text-[10px] text-zinc-500 mt-1">{`Aparece cuando el cliente elige "Recoger en tienda". Vacío = lo coordinamos por correo/teléfono.`}</p>
            </div>
          </div>
          <div className="flex justify-end">
            <Button
              type="submit"
              disabled={savingDelivery}
              variant="outline"
              data-testid="mp-save-delivery"
            >
              {savingDelivery ? "Guardando…" : "Guardar entrega"}
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
}

function AlendraAISettings() {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/alendra/settings");
      setData(data);
      setSelected(data.current_model);
    } catch (err) {
      console.warn("Alendra settings load failed", err);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!selected || selected === data?.current_model) return;
    setSaving(true);
    try {
      await api.put("/alendra/settings", { model: selected });
      toast.success(`Modelo de Alendra AI actualizado a ${selected}`);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar");
    } finally { setSaving(false); }
  };

  if (!data) return null;

  const groups = {
    premium: "Premium (mejor calidad)",
    standard: "Estándar",
    economy: "Económicos (más barato)",
    reasoning: "Reasoning (análisis profundo)",
  };

  return (
    <section className="border border-[#002FA7]/20 bg-gradient-to-br from-[#002FA7]/5 to-transparent rounded-lg" data-testid="alendra-settings">
      <div className="px-6 pt-6 pb-4 space-y-4">
        <div className="flex items-center gap-2">
          <Sparkle size={20} weight="fill" className="text-[#002FA7]"/>
          <h3 className="font-heading text-lg font-semibold text-[#002FA7]">Alendra AI</h3>
        </div>
        <p className="text-xs text-zinc-600">
          Asistente IA por producto. Genera descripciones, publicaciones por canal y chatea contextualmente.
          Usa tu API key de OpenAI (no consume créditos del Universal Key).
        </p>

        <div className={`p-3 rounded border text-xs ${data.api_key_configured ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-amber-50 border-amber-200 text-amber-900"}`} data-testid="alendra-key-status">
          {data.api_key_configured ? (
            <span><CheckCircle size={14} className="inline mb-0.5 mr-1"/>API key configurada en backend (.env)</span>
          ) : (
            <span><Warning size={14} className="inline mb-0.5 mr-1"/>OPENAI_API_KEY NO configurada. Pide al admin agregarla en <span className="font-mono">backend/.env</span> para activar Alendra.</span>
          )}
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wider">Modelo OpenAI</Label>
          <p className="text-[11px] text-zinc-500 mb-2">Cambia entre modelos para calibrar costo vs calidad. Los modelos premium dan mejores textos; los económicos cuestan ~10x menos por token.</p>
          <div className="space-y-3">
            {Object.entries(groups).map(([tier, label]) => {
              const models = data.available_models.filter((m) => m.tier === tier);
              if (!models.length) return null;
              return (
                <div key={tier}>
                  <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5">{label}</div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
                    {models.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        data-testid={`alendra-model-${m.id}`}
                        onClick={() => setSelected(m.id)}
                        className={`px-3 py-2 rounded border text-left text-xs transition ${selected === m.id ? "border-[#002FA7] bg-[#002FA7] text-white" : "border-zinc-200 hover:border-[#002FA7]/40 hover:bg-[#002FA7]/5"}`}
                      >
                        <div className="font-semibold font-mono">{m.id}</div>
                        <div className={`text-[10px] mt-0.5 ${selected === m.id ? "text-white/80" : "text-zinc-500"}`}>{m.label}</div>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        <div className="flex items-center gap-2 pt-2">
          <Button
            onClick={save}
            disabled={saving || !selected || selected === data.current_model}
            className="bg-[#002FA7] hover:bg-[#002277] text-white"
            data-testid="alendra-save-model"
          >
            {saving ? "Guardando…" : "Guardar modelo"}
          </Button>
          <span className="text-[11px] text-zinc-500">Actual: <b className="font-mono">{data.current_model}</b></span>
        </div>
      </div>
    </section>
  );
}
